import React, { useEffect, useState } from 'react';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { HomeContent } from './components/HomeContent';
import { MoviesContent } from './components/MoviesContent';
import { PlayerBar } from './components/PlayerBar';
import { PlaylistView } from './components/PlaylistView';
import { SearchPopup } from './components/SearchPopup';
import { UserMenu } from './components/UserMenu';
import { SettingsMenu } from './components/SettingsMenu';
import { NotificationsPopup } from './components/NotificationsPopup';
import { LibraryPlaylistView } from './components/LibraryPlaylistView';
import { MobileNavigation } from './components/MobileNavigation';
export function App() {
  // Navigation state
  const [currentView, setCurrentView] = useState('home');
  const [navigationHistory, setNavigationHistory] = useState(['home']);
  const [historyIndex, setHistoryIndex] = useState(0);
  // UI state for popups and modals
  const [openPlaylist, setOpenPlaylist] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLibraryPlaylist, setShowLibraryPlaylist] = useState(null);
  // Player state
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(105); // 1:45 in seconds
  const [volume, setVolume] = useState(80);
  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  // Check if the viewport is mobile size
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Initial check
    checkIfMobile();
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  // Navigation handlers
  const handleNavigate = view => {
    setCurrentView(view);
    // Update history
    const newHistory = [...navigationHistory.slice(0, historyIndex + 1), view];
    setNavigationHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    // Close any open modals/views
    closeAllModals();
  };
  const handleGoBack = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCurrentView(navigationHistory[historyIndex - 1]);
      closeAllModals();
    }
  };
  const handleGoForward = () => {
    if (historyIndex < navigationHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCurrentView(navigationHistory[historyIndex + 1]);
      closeAllModals();
    }
  };
  // Modal handlers
  const closeAllModals = () => {
    setOpenPlaylist(null);
    setOpenAlbum(null);
    setShowSearchPopup(false);
    setShowUserMenu(false);
    setShowSettingsMenu(false);
    setShowNotifications(false);
    setShowLibraryPlaylist(null);
  };
  const handlePlaylistClick = playlist => {
    setOpenPlaylist(playlist);
    // Update navigation history
    const newHistory = [...navigationHistory.slice(0, historyIndex + 1), 'playlist'];
    setNavigationHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCurrentView('playlist');
  };
  const handleAlbumClick = album => {
    setOpenAlbum(album);
    // Update navigation history
    const newHistory = [...navigationHistory.slice(0, historyIndex + 1), 'album'];
    setNavigationHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCurrentView('album');
  };
  const handleLibraryItemClick = item => {
    setShowLibraryPlaylist(item);
    // Update navigation history
    const newHistory = [...navigationHistory.slice(0, historyIndex + 1), 'libraryItem'];
    setNavigationHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCurrentView('libraryItem');
  };
  // Player handlers
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  const handleNext = () => {
    setCurrentTime(0);
    setIsPlaying(true);
  };
  const handlePrevious = () => {
    setCurrentTime(0);
    setIsPlaying(true);
  };
  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };
  const handleProgressChange = (progress: number) => {
    setCurrentTime(progress / 100 * 200);
  };
  // Determine what content to render
  const renderMainContent = () => {
    if (currentView === 'home') {
      return <HomeContent onPlaylistClick={handlePlaylistClick} onAlbumClick={handleAlbumClick} data-id="element-0" />;
    } else if (currentView === 'movies') {
      return <MoviesContent data-id="element-1" />;
    } else if (currentView === 'playlist' && openPlaylist) {
      return <PlaylistView playlist={openPlaylist} onBack={() => handleGoBack()} data-id="element-2" />;
    } else if (currentView === 'album' && openAlbum) {
      return <PlaylistView playlist={openAlbum} isAlbum onBack={() => handleGoBack()} data-id="element-3" />;
    } else if (currentView === 'libraryItem' && showLibraryPlaylist) {
      return <LibraryPlaylistView item={showLibraryPlaylist} onBack={() => handleGoBack()} data-id="element-4" />;
    } else if (currentView === 'search') {
      return <SearchPopup onClose={() => handleNavigate('home')} data-id="element-5" />;
    } else {
      return <HomeContent onPlaylistClick={handlePlaylistClick} onAlbumClick={handleAlbumClick} data-id="element-6" />;
    }
  };
  return <div className="flex flex-col h-screen bg-black text-white" data-id="element-7">
      <div className="flex flex-1 overflow-hidden" data-id="element-8">
        {/* Show sidebar only on desktop */}
        {!isMobile && <Sidebar onNavigate={handleNavigate} currentView={currentView} onLibraryItemClick={handleLibraryItemClick} data-id="element-9" />}
        <div className="flex flex-col flex-1 overflow-hidden bg-[#121212]" data-id="element-10">
          <Header onBack={handleGoBack} onForward={handleGoForward} canGoBack={historyIndex > 0} canGoForward={historyIndex < navigationHistory.length - 1} onSearchClick={() => setShowSearchPopup(true)} onNotificationsClick={() => setShowNotifications(!showNotifications)} onSettingsClick={() => setShowSettingsMenu(!showSettingsMenu)} onUserClick={() => setShowUserMenu(!showUserMenu)} isMobile={isMobile} data-id="element-11" />
          <main className="flex-1 overflow-y-auto pb-16 md:pb-0" data-id="element-12">
            {renderMainContent()}
          </main>
        </div>
      </div>
      {/* Modals and popups */}
      {showSearchPopup && <SearchPopup onClose={() => setShowSearchPopup(false)} data-id="element-13" />}
      {showUserMenu && <UserMenu onClose={() => setShowUserMenu(false)} data-id="element-14" />}
      {showSettingsMenu && <SettingsMenu onClose={() => setShowSettingsMenu(false)} data-id="element-15" />}
      {showNotifications && <NotificationsPopup onClose={() => setShowNotifications(false)} data-id="element-16" />}
      {/* Mobile Navigation */}
      {isMobile && <MobileNavigation currentView={currentView} onNavigate={handleNavigate} data-id="element-17" />}
      <PlayerBar songTitle="Blinding Lights" artistName="The Weeknd" albumCover="https://i.scdn.co/image/ab67616d00001e02ef10c30dfca2f5c3a4426b03" currentTime={`${Math.floor(currentTime / 60)}:${String(currentTime % 60).padStart(2, '0')}`} duration="3:20" progress={currentTime / 200 * 100} isPlaying={isPlaying} volume={volume} onPlayPause={handlePlayPause} onNext={handleNext} onPrevious={handlePrevious} onVolumeChange={handleVolumeChange} onProgressChange={handleProgressChange} isMobile={isMobile} data-id="element-18" />
    </div>;
}